import React from 'react';
import { motion } from 'framer-motion';

/**
 * Welcome screen rendered as the first view of both web2app funnels
 * (/funnel-auth and /funnel-auth/intro), ahead of their step shells — in the
 * `control` arm of the welcome experiment (hooks/useWelcomeVariant.js). The
 * `no-welcome` arm skips it,
 * and `no-welcome` renders nothing here at all.
 *
 * By default the button starts making the character (`onContinue`) and the
 * link under it is for visitors who want to see more first (`onExplore`,
 * which falls back to `onContinue` for a funnel with nothing to explore).
 * `exploreOnly` is the intro funnel's version: one button, "See how Pixley
 * works", straight into its explainer slides, and no link.
 */

export const welcomeContainerVariants = {
  enter: {},
  center: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

export const welcomeItemVariants = {
  enter: { opacity: 0, y: 18 },
  center: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m8.5 4.5 7.5 7.5-7.5 7.5" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The welcome screen's stage — night-blue phone frame, the art pinned to its
 * top, the logo — with whatever panel goes on it as children. `blank` renders
 * just the empty frame: what a first-time visitor sees for the moment the
 * welcome experiment's flag is still being fetched, so no arm's opening
 * flashes before the decided one.
 */
export const FunnelWelcomeFrame = ({ blank = false, children }) => (
  <main className="fw-bg">
    <FunnelWelcomeStyles />
    <div className="fw-frame" data-testid={blank ? 'funnel-welcome-pending' : undefined}>
      {!blank && (
        <>
          <img
            className="fw-art"
            src="/assets/funnel/welcome-bg.webp"
            width="1448"
            height="1086"
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="fw-shade" aria-hidden="true" />
          <img
            className="fw-logo"
            src="/assets/funnel/pixley-logo-3d.webp"
            width="900"
            height="300"
            alt="Pixley"
            loading="eager"
            decoding="async"
          />
        </>
      )}
      {children}
    </div>
  </main>
);

// What a visitor from a Google ad is told on landing, in the ad's own words:
// Google's pricing policy wants the price, the renewal and the cancellation
// visible on the page the ad opens, not three screens in. Control's offer
// (backend FUNNEL_OFFER_VARIANTS, hooks/useOfferVariant pins Google visitors
// to it); change both together.
export const GOOGLE_LANDING_TERMS = '$7.99 for your first month, then $14.99/mo. Cancel anytime.';
export const GOOGLE_LANDING_SAFETY = 'No ads · Parent controlled · Made for ages 2–8';

const FunnelWelcomePage = ({ onContinue, onExplore = onContinue, exploreOnly = false, terms = null, safety = null }) => (
  <FunnelWelcomeFrame>
    <motion.section
      className="fw-panel"
      variants={welcomeContainerVariants}
      initial="enter"
      animate="center"
      aria-labelledby="fw-title"
    >
      <motion.h1 id="fw-title" className="fw-title" variants={welcomeItemVariants}>
        <span>Your child</span>
        <span className="fw-title-accent">belongs in the story.</span>
      </motion.h1>

      <motion.p className="fw-subtitle" variants={welcomeItemVariants}>
        Create a character for the<br />stories you make together.
      </motion.p>

      <motion.button
        type="button"
        className="fw-cta"
        onClick={exploreOnly ? onExplore : onContinue}
        variants={welcomeItemVariants}
        whileTap={{ scale: 0.985 }}
      >
        <span>{exploreOnly ? 'See how Pixley works' : 'Create their character'}</span>
        <span className="fw-cta-arrow"><ArrowIcon /></span>
      </motion.button>

      {!exploreOnly && (
        <motion.button type="button" className="fw-link" onClick={onExplore} variants={welcomeItemVariants}>
          See how Pixley works
        </motion.button>
      )}

      {(terms || safety) && (
        <motion.p className="fw-terms" variants={welcomeItemVariants} data-testid="funnel-welcome-terms">
          {terms && <span>{terms}</span>}
          {safety && <span className="fw-terms-safety">{safety}</span>}
        </motion.p>
      )}
    </motion.section>
  </FunnelWelcomeFrame>
);

const FunnelWelcomeStyles = () => (
  <style>{`
    .fw-bg {
      min-height: 100vh;
      min-height: 100dvh;
      background:
        radial-gradient(1000px 700px at 80% 0%, rgba(124,92,255,0.1), transparent 60%),
        linear-gradient(180deg, #F5FAFF 0%, #E8F1FF 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
    }

    /* container-type makes this the reference box for the cqw units below. On
       a phone the frame is the whole screen; on desktop it's a ~400px phone
       mockup inside the window, and cqw keeps the layout a faithful scaled copy
       of the phone one (vw would measure the window and blow the type up). */
    .fw-frame {
      position: relative;
      container-type: inline-size;
      width: 100%;
      height: 100vh;
      height: 100dvh;
      min-height: 600px;
      /* The art is wider than the frame. overflow:hidden still lets the frame
         scroll sideways programmatically — a tapped answer card taking focus
         did exactly that, shoving the whole screen left — where overflow:clip
         makes it un-scrollable. hidden stays as the fallback for older Safari.
         (No backticks in here: this CSS lives in a template literal.) */
      overflow: hidden;
      overflow: clip;
      background: #0A1432;
      isolation: isolate;
    }

    @media (min-aspect-ratio: 3/4) {
      .fw-frame {
        width: auto;
        height: min(calc(100dvh - 48px), 920px);
        min-height: min(calc(100dvh - 48px), 600px);
        aspect-ratio: 390 / 844;
        border-radius: 32px;
        box-shadow: 0 24px 60px rgba(11,26,51,0.18), 0 4px 14px rgba(11,26,51,0.08);
      }
    }

    /* The art is landscape; it's pinned to the very top (no band of frame
       colour above it) and scaled so the panda and tiger fill the width, with
       the rest of the screen falling to night blue under the copy. */
    .fw-art {
      position: absolute;
      z-index: 0;
      top: 0;
      left: 50%;
      width: 153cqw;
      /* Tailwind's base styles cap every img at max-width: 100%, which would
         shrink the art to the screen's width — shorter than the fade below is
         placed for, leaving a hard edge where the picture stops */
      max-width: none;
      height: auto;
      transform: translateX(-50%);
      pointer-events: none;
      user-select: none;
    }
    .fw-shade {
      position: absolute;
      z-index: 1;
      inset: 0;
      pointer-events: none;
      background:
        linear-gradient(180deg, rgba(10,20,50,0.35) 0%, rgba(10,20,50,0) 14%),
        linear-gradient(180deg, rgba(10,20,50,0) 78cqw, rgba(10,20,50,0.72) 100cqw, #0A1432 117cqw);
    }

    .fw-logo {
      position: absolute;
      z-index: 2;
      top: 4cqw;
      left: 50%;
      width: 68cqw;
      height: auto;
      transform: translateX(-50%);
      filter: drop-shadow(0 6px 14px rgba(4,12,34,0.45));
    }

    .fw-panel {
      position: absolute;
      z-index: 3;
      left: 0; right: 0; bottom: 0;
      padding:
        0
        calc(5.5cqw + env(safe-area-inset-right, 0px))
        calc(17cqw + env(safe-area-inset-bottom, 0px))
        calc(5.5cqw + env(safe-area-inset-left, 0px));
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      color: #FFF;
    }

    /* "Your child" is set larger than the line under it, as in the design;
       0.7em is what keeps "belongs in the story." to one line at phone width */
    .fw-title {
      margin: 0;
      font-size: 14.2cqw;
      font-weight: 700;
      line-height: 1.05;
      letter-spacing: -0.025em;
      text-shadow: 0 3px 16px rgba(3,12,34,0.55);
    }
    .fw-title > span { display: block; }
    .fw-title-accent {
      font-size: 0.7em;
      line-height: 1.2;
      background: linear-gradient(180deg, #7DBBFF 0%, #4A93FF 100%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      /* text-shadow would paint through the clipped text; use a drop shadow */
      text-shadow: none;
      filter: drop-shadow(0 3px 12px rgba(3,12,34,0.5));
    }

    .fw-subtitle {
      margin: 4.6cqw 0 0;
      font-family: 'Inter', 'Fredoka', sans-serif;
      font-size: 5.3cqw;
      font-weight: 400;
      line-height: 1.38;
      color: rgba(255,255,255,0.92);
      text-shadow: 0 2px 10px rgba(3,12,34,0.6);
    }

    .fw-cta {
      position: relative;
      width: 100%;
      min-height: 15.5cqw;
      margin-top: 7cqw;
      padding: 3cqw 14cqw;
      border: none;
      border-radius: 999px;
      background: linear-gradient(90deg, #1F74FD 0%, #3B8CFF 100%);
      color: #FFF;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 5.9cqw;
      font-weight: 700;
      line-height: 1;
      cursor: pointer;
      box-shadow: 0 12px 30px rgba(31,116,253,0.45), inset 0 1px 0 rgba(255,255,255,0.22);
      transition: box-shadow 180ms ease, filter 180ms ease;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }
    .fw-cta:hover { box-shadow: 0 16px 34px rgba(31,116,253,0.55), inset 0 1px 0 rgba(255,255,255,0.22); }
    .fw-cta:focus-visible { outline: 4px solid rgba(125,187,255,0.5); outline-offset: 4px; }
    .fw-cta-arrow {
      position: absolute;
      top: 50%;
      right: 7cqw;
      display: grid;
      place-items: center;
      width: 6.4cqw;
      height: 6.4cqw;
      transform: translateY(-50%);
    }
    .fw-cta-arrow svg { width: 100%; height: 100%; }

    .fw-link {
      margin-top: 4.6cqw;
      padding: 1cqw 2cqw;
      background: none;
      border: none;
      font-family: 'Inter', 'Fredoka', sans-serif;
      font-size: 4.6cqw;
      font-weight: 500;
      color: #7DB6FF;
      text-decoration: underline;
      text-underline-offset: 0.8cqw;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    /* The Google-ad landing's price and safety lines: plain text, no
       overlay, readable white on the night panel, under the button */
    .fw-terms {
      margin: 14px 0 0;
      font-size: 13.5px;
      line-height: 1.45;
      text-align: center;
      color: rgba(255,255,255,0.92);
      text-shadow: 0 1px 2px rgba(0,0,0,0.35);
    }
    .fw-terms span { display: block; }
    .fw-terms-safety { margin-top: 4px; color: rgba(255,255,255,0.78); }
    .fw-link:hover { color: #A6CEFF; }
    .fw-link:focus-visible { outline: 3px solid rgba(125,187,255,0.5); outline-offset: 2px; border-radius: 6px; }

    /* Short phones (SE and friends): pull the copy up the art rather than
       letting it run off the bottom */
    @media (max-height: 640px) and (max-aspect-ratio: 3/4) {
      .fw-panel { padding-bottom: calc(10cqw + env(safe-area-inset-bottom, 0px)); }
      .fw-title { font-size: 13cqw; }
      .fw-subtitle { margin-top: 3.4cqw; }
      .fw-cta { margin-top: 5cqw; min-height: 14cqw; }
      .fw-link { margin-top: 3cqw; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fw-cta { transition: none; }
    }
  `}</style>
);

export default FunnelWelcomePage;
